import type { RefObject } from 'react';
import React, { Children, cloneElement, Component, createRef } from 'react';
import cn from 'classnames';
import {
    subscribeBodyOverflowShift,
    unsubscribeBodyOverflowShift,
} from '@alfa-bank/site-use-lock-body-scroll/events';
import { Stack, stackingOrder } from '@alfa-bank/site-stack/src';

import { DEFAULT_POSITION, DEFAULT_TEST_ID, RESIZE, SCROLL, throttleTimeout } from './config';
import type { IState, TSticky, TStyle } from './models';
import { detectPositionInViewport } from './helpers';

export class Sticky extends Component<TSticky, IState> {
    static defaultProps = {
        styles: {} as TStyle,
        className: null,
        offsetBottom: 0,
        offsetTop: 0,
        dataTestId: DEFAULT_TEST_ID,
    };

    private stickyHeight: number;

    private sticky: RefObject<HTMLDivElement>;

    readonly throttleHandle: () => void;

    constructor(props: TSticky) {
        super(props);

        this.state = {
            bodyOverflowShift: 0,
            isSticked: false,
        };
        this.sticky = createRef<HTMLDivElement>();
        this.stickyHeight = 0;
        this.calculateIsSticked = this.calculateIsSticked.bind(this);
        this.hideSticky = this.hideSticky.bind(this);
        this.throttleHandle = props.throttle(this.calculateIsSticked, throttleTimeout);
    }

    componentDidMount() {
        window.addEventListener(SCROLL, this.throttleHandle);
        window.addEventListener(RESIZE, this.throttleHandle);

        subscribeBodyOverflowShift(this.handleChangeOverflowShift);

        const { targetSelectors = [], targetSelectorsForHide = [] } = this.props;
        // Отображаем sticky после того как элементы из
        // targetSelectors, targetSelectorsForHide будут спозиционированы
        if (targetSelectors.length > 0 || targetSelectorsForHide.length > 0) {
            setTimeout(() => {
                this.calculateIsSticked();
            }, 0);

            return;
        }

        this.calculateIsSticked();
    }

    componentWillUnmount() {
        window.removeEventListener(SCROLL, this.throttleHandle);
        window.removeEventListener(RESIZE, this.throttleHandle);
        unsubscribeBodyOverflowShift(this.handleChangeOverflowShift);
    }

    handleChangeOverflowShift = (bodyOverflowShift: number) => {
        this.setState({ bodyOverflowShift });
    };

    calculateIsSticked() {
        const { position = DEFAULT_POSITION, targetSelectors = [], elLocation } = this.props;

        const isSticked = targetSelectors.every((targetSelector: string) => {
            const element = document.querySelector(targetSelector);

            if (!element) {
                return true;
            }

            const { bottom, top } = element.getBoundingClientRect();

            const offsetTop = this.props.offsetTop || 0;

            /* istanbul ignore next */
            if (elLocation) {
                return this.defineByLocation(top, bottom);
            }

            /* istanbul ignore next */
            return (position === DEFAULT_POSITION ? top + -offsetTop : bottom) <= 0;
        });

        this.setState({ isSticked });
        this.hideSticky();
    }

    defineByLocation = (top: number, bottom: number) => {
        const { elLocation } = this.props;

        const {
            isElEntireInViewport,
            isElPartlyInViewport,
            isViewportBottomInsideEl,
            isViewportInsideEl,
            isViewportTopInsideEl,
        } = detectPositionInViewport(top, bottom);

        if (elLocation === 'full') {
            return isElEntireInViewport || isViewportInsideEl;
        }
        if (elLocation === 'viewportTopInsideEl') {
            return isViewportTopInsideEl;
        }
        if (elLocation === 'viewportBottomInsideEl') {
            return isViewportBottomInsideEl;
        }

        // elLocation === 'partly' или elLocation === undefined
        return isElPartlyInViewport || isViewportInsideEl;
    };

    hideSticky() {
        const {
            offsetBottom = 0,
            targetSelectors = [],
            targetSelectorsForHide = [],
            elLocation,
            offsetTop,
        } = this.props;

        if (!targetSelectors.length && !targetSelectorsForHide.length) {
            return;
        }
        // перенес из componentDidMount, так как на фича-ветке и на проде
        // при первом рендере высота стики-меню равна нулю =>
        // this.sticky.current.offsetHeight = 0
        if (this.sticky.current) {
            this.stickyHeight = this.sticky.current.offsetHeight;
        }

        const isPositionToHide = targetSelectorsForHide.some(targetSelectorForHide => {
            const element = document.querySelector(targetSelectorForHide);

            if (!element) {
                return false;
            }

            const { bottom, top } = element.getBoundingClientRect();
            const offset = offsetTop || 0;

            if (elLocation) {
                return this.defineByLocation(top, bottom);
            }

            // elLocation === undefined
            return top - offset <= this.stickyHeight && bottom + offsetBottom > this.stickyHeight;
        });

        if (isPositionToHide) {
            this.setState({ isSticked: false });
        }
    }

    render() {
        const {
            children,
            className,
            position = DEFAULT_POSITION,
            positionX = 'left',
            styles,
            dataTestId = DEFAULT_TEST_ID,
            shadow = true,
            width = 'full',
            offsetTop,
            defaultZIndex = stackingOrder.DEFAULT,
        } = this.props;

        const { bodyOverflowShift, isSticked } = this.state;
        const isTopPosition = position === 'top';

        const classNames = cn(
            styles[`${isTopPosition ? 'positionTop' : 'positionBottom'}`],
            {
                [styles[`positionX${positionX}`]]: isSticked,
                [styles.shadow]: shadow,
                [styles.sticked]: isSticked,
                [styles.hidden]: !isSticked,
            },
            className
        );

        const isOneChild = Children.count(children) === 1;

        const widthStyle = (() => {
            switch (width) {
                case 'full':
                    return `calc(100% - ${bodyOverflowShift}px)`;
                case 'content':
                    return 'fit-content';
            }
        })();

        return (
            <Stack value={defaultZIndex}>
                {zIndex => (
                    <div
                        ref={this.sticky}
                        className={classNames}
                        data-test-id={dataTestId}
                        style={{
                            width: widthStyle,
                            top: isTopPosition ? offsetTop : undefined,
                            bottom: isTopPosition ? undefined : offsetTop,
                            zIndex,
                        }}
                    >
                        {isOneChild
                            ? cloneElement(children, { isStickedByParent: isSticked })
                            : children}
                    </div>
                )}
            </Stack>
        );
    }
}
